import { useEffect, useRef } from 'react';
import { MockPriceSocket } from './MockPriceSocket';
import { ReplayPriceSocket, type ReplayCandle } from './ReplayPriceSocket';
import { useInstrumentRealtime } from '@/lib/realtime';
import { coerceTimeSeconds, parseInstrumentTick, type PriceTick } from '@/lib/instrumentTick';
import { usePredictionMarketInstrumentsGetSnapshots } from '@/lib/endpoints';
import type { InstrumentSnapshotsResponseDto } from '@/lib/models';
import { LiveChartEngine } from './liveChartEngine';

// Dev flag: replay `src/mock/btc_5min_1s.json` through the chart instead of the
// live feed, for offline testing. See ReplayPriceSocket.
const REPLAY_ENABLED = import.meta.env.VITE_CHART_REPLAY === 'true';

// Synthetic-history length (seconds) — the FALLBACK seed anchored at the first
// real tick, used only when the real history request fails or returns nothing.
const SEED_COUNT = 61;

// Real-history seed: how many latest Plaee snapshots to request (~1/s cadence,
// so this comfortably covers the 60s window), and how long to hold buffered
// live ticks for it before falling back to the synthetic seed.
const HISTORY_SNAPSHOT_LIMIT = 90;
const HISTORY_TIMEOUT_MS = 2_500;
const HISTORY_TICK_BUFFER_MAX = 200;

/** Map the snapshots response onto chart ticks (drops price/time-less rows). */
function snapshotsToTicks(data: InstrumentSnapshotsResponseDto): PriceTick[] {
  const out: PriceTick[] = [];
  for (const s of data.snapshots ?? []) {
    if (typeof s.lastPrice !== 'number' || !s.sendingTime) continue;
    const timeMs = new Date(s.sendingTime).getTime();
    if (!Number.isFinite(timeMs)) continue;
    out.push({ price: s.lastPrice, time: timeMs / 1000 });
  }
  return out;
}

/**
 * Data for the chart's frozen "past view": a pre-built static dollar series plus
 * the settled "Final Price" line. When passed, the chart renders this seed once
 * and starts **no** live/mock socket — it's a still image of a closed window.
 */
export interface FrozenChartData {
  /** Pre-built dollar candles to seed statically (ascending, unix seconds). */
  seedCandles: PriceTick[];
  /** Settled price → the "Final Price" line; `null` hides that line. */
  finalPrice: number | null;
  /** Already-formatted chip label for the final-price line (e.g. `$67,000.50`). */
  finalPriceLabel: string;
}

/**
 * Self-contained hook for the live price chart.
 *
 * The rendering is a direct port of the Polymarket 5-minute chart recreation
 * (see {@link LiveChartEngine}): a sliding 60-second window drawn per-frame on
 * a plain canvas — spring-tracked head, min-span autoscale, animated in-pane
 * price labels, clamping Target chip, pulsing head. The hook owns only the
 * plumbing: canvas lifecycle, feed wiring (live / mock / replay / frozen), and
 * seeding.
 *
 * Data source: if `instrumentSymbol` is provided, the chart streams live ticks
 * from the Plaee `prediction-instruments` firehose (via the backend Socket.IO
 * gateway). Otherwise it falls back to {@link MockPriceSocket} so the widget
 * keeps rendering during local dev / offline.
 *
 * Attach the returned `containerRef` to a div to render the chart.
 */
export function useLightweightChart(
  basePrice: number,
  instrumentSymbol?: string | null,
  targetPrice?: number | null,
  interactive = true,
  frozen?: FrozenChartData | null,
) {
  // Frozen (past) view: seed a static series, draw the Final Price line, and run
  // no socket. Presence of `frozen` is the mode switch.
  const isFrozen = Boolean(frozen);
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<LiveChartEngine | null>(null);
  // Latest target/strike held in a ref so the engine (built once per mount)
  // always reads the current value — switching resolution just moves the line.
  const targetPriceRef = useRef<number | null>(targetPrice ?? null);
  useEffect(() => {
    targetPriceRef.current = targetPrice ?? null;
  }, [targetPrice]);
  // Final-price line (frozen view) — read by the engine each frame.
  const finalPriceRef = useRef<number | null>(frozen?.finalPrice ?? null);
  const finalLabelRef = useRef<string>(frozen?.finalPriceLabel ?? '');
  useEffect(() => {
    finalPriceRef.current = frozen?.finalPrice ?? null;
    finalLabelRef.current = frozen?.finalPriceLabel ?? '';
  }, [frozen]);
  // Static seed for the frozen view. Read inside the build effect at mount; the
  // parent gates rendering until the past-market data is loaded and remounts the
  // chart (via `key`) when the selection changes, so this is stable per mount.
  const seedCandlesRef = useRef<PriceTick[] | null>(frozen?.seedCandles ?? null);
  seedCandlesRef.current = frozen?.seedCandles ?? null;
  const tickHandlerRef = useRef<(tick: PriceTick) => void>(() => {});
  // Latest basePrice held in a ref so changing it does NOT re-create the chart
  // (it only anchors the mock socket / min-span fallback).
  const basePriceRef = useRef(basePrice);
  useEffect(() => {
    basePriceRef.current = basePrice;
  }, [basePrice]);

  // Live feed (no-op when symbol is null — falls back to mock below). Subscribes
  // to the Plaee `prediction-instruments` Centrifugo channel directly; the hook
  // filters by `instrumentCode` and emits canonical {@link InstrumentTick}s.
  useInstrumentRealtime(REPLAY_ENABLED || isFrozen ? null : (instrumentSymbol ?? null), (tick) => {
    const price = typeof tick.lastPrice === 'number' ? tick.lastPrice : null;
    if (price === null) return;
    tickHandlerRef.current({ price, time: coerceTimeSeconds(tick.ts) });
  });

  // Real history for the live path: the last ~90 Plaee snapshots seed the
  // window on load/refresh, so the chart opens showing what the price actually
  // did (the synthetic random walk remains only as an error/timeout fallback).
  const isLiveFeed = Boolean(instrumentSymbol) && !REPLAY_ENABLED && !isFrozen;
  const historyQuery = usePredictionMarketInstrumentsGetSnapshots(
    instrumentSymbol ?? '',
    { limit: HISTORY_SNAPSHOT_LIMIT },
    // The window is the last ~60 seconds, so cached history is useless — always
    // refetch on mount and only seed from a fresh response (see the gate below).
    { query: { enabled: isLiveFeed, staleTime: 0, refetchOnMount: 'always' } },
  );
  // The build effect (below) registers how to consume the result; the refs
  // decouple query completion from effect ordering — whichever side is ready
  // last picks the result up (covers strict-mode remounts and cached settles).
  const historyResultRef = useRef<(ticks: PriceTick[] | null) => void>(() => {});
  const historyStateRef = useRef<{ settled: boolean; ticks: PriceTick[] | null }>({ settled: false, ticks: null });
  const historySettled = isLiveFeed && !historyQuery.isFetching && (historyQuery.isFetched || historyQuery.isError);
  const historyData = historySettled && !historyQuery.isError ? historyQuery.data : undefined;
  useEffect(() => {
    if (!historySettled) {
      // New instrument / refetch in flight — never leak a previous result.
      historyStateRef.current = { settled: false, ticks: null };
      return;
    }
    const ticks = historyData ? snapshotsToTicks(historyData) : null;
    historyStateRef.current = { settled: true, ticks };
    historyResultRef.current(ticks);
  }, [historySettled, historyData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // --- Canvas + engine ---
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    container.appendChild(canvas);

    const engine = new LiveChartEngine(canvas, {
      symbol: instrumentSymbol ?? null,
      anchorPrice: basePriceRef.current,
      getTargetPrice: () => targetPriceRef.current,
      getFinalPrice: () => {
        const price = finalPriceRef.current;
        return price != null && isFrozen ? { price, label: finalLabelRef.current } : null;
      },
      frozen: isFrozen,
    });
    engineRef.current = engine;

    let seeded = false;

    // Synthesize a SEED_COUNT-long random walk ending at `anchorPrice` at
    // `anchorTimeSec` (1s cadence), so the live path has history to show
    // before real history exists. Constants mirror MockPriceSocket.step.
    function buildRandomWalkSeed(anchorPrice: number, anchorTimeSec: number): PriceTick[] {
      const FLAT_TICK_CHANCE = 0.4;
      const VELOCITY_RANDOM_SCALE = 0.8;
      const VELOCITY_DECAY = 0.92;
      let price = anchorPrice;
      let velocity = 0;
      const backwards: PriceTick[] = [];
      for (let i = 1; i < SEED_COUNT; i++) {
        if (Math.random() >= FLAT_TICK_CHANCE) {
          velocity += (Math.random() - 0.5) * VELOCITY_RANDOM_SCALE;
          velocity *= VELOCITY_DECAY;
          price -= velocity;
        }
        backwards.push({ price, time: anchorTimeSec - i });
      }
      const result: PriceTick[] = [];
      for (let i = backwards.length - 1; i >= 0; i--) result.push(backwards[i]);
      result.push({ price: anchorPrice, time: anchorTimeSec });
      return result;
    }

    // --- Live-path seeding state ---
    // Real history is the seed of record; live ticks that arrive while it loads
    // are buffered and folded into the seed. The synthetic walk is only the
    // fallback for an errored/empty/hung history request.
    const pendingLiveTicks: PriceTick[] = [];
    let liveHistorySettled = false;
    let historyTimeout: ReturnType<typeof setTimeout> | null = null;

    function seedLive(history: PriceTick[] | null) {
      if (seeded || cancelled) return;
      if (historyTimeout !== null) {
        clearTimeout(historyTimeout);
        historyTimeout = null;
      }
      liveHistorySettled = true;
      if (history && history.length > 0) {
        engine.seed([...history, ...pendingLiveTicks]);
        seeded = true;
      } else if (pendingLiveTicks.length > 0) {
        // No usable history — anchor the synthetic walk at the newest live tick.
        const anchor = pendingLiveTicks[pendingLiveTicks.length - 1];
        engine.seed(buildRandomWalkSeed(anchor.price, anchor.time));
        seeded = true;
      }
      // else: no history AND no tick yet — the first tick seeds synthetically.
      pendingLiveTicks.length = 0;
    }

    // --- Tick pipeline (shared by mock + live feeds) ---
    const seedBuffer: PriceTick[] = [];
    const onTick = (tick: PriceTick) => {
      if (!seeded) {
        if (instrumentSymbol && !REPLAY_ENABLED) {
          if (!liveHistorySettled) {
            // Hold ticks for the history seed — but never blank the chart on a
            // hung request: past the timeout, fall back to the synthetic walk.
            pendingLiveTicks.push(tick);
            if (pendingLiveTicks.length > HISTORY_TICK_BUFFER_MAX) pendingLiveTicks.shift();
            if (historyTimeout === null) {
              historyTimeout = setTimeout(() => seedLive(null), HISTORY_TIMEOUT_MS);
            }
            return;
          }
          // History settled empty/error before any tick arrived.
          engine.seed(buildRandomWalkSeed(tick.price, tick.time));
          seeded = true;
          return;
        }
        // Mock / replay path: accumulate the synchronous seed burst. The burst
        // carries historical timestamps and ends at "now" — seed exactly when
        // it reaches the present.
        seedBuffer.push(tick);
        if (seedBuffer.length >= 2 && tick.time >= Date.now() / 1000 - 1.5) {
          engine.seed(seedBuffer);
          seeded = true;
        }
        return;
      }
      engine.pushTick(tick);
    };

    tickHandlerRef.current = onTick;
    historyResultRef.current = seedLive;
    // If the history query settled before this effect ran (remount with a
    // fresh-enough response), consume it now instead of waiting on a re-fire.
    if (historyStateRef.current.settled) seedLive(historyStateRef.current.ticks);

    // If no live symbol is provided, fall back to the mock socket so the widget
    // keeps moving during dev / offline. With a symbol, the chart stays blank
    // until the first real tick arrives and self-seeds at the real price.
    //
    // The mock emits the production wire shape — same parser path as live.
    let mock: MockPriceSocket | null = null;
    let replay: ReplayPriceSocket | null = null;
    let cancelled = false;
    if (isFrozen) {
      // Past view: seed the static series once and start no socket. Empty seed
      // (a window with no candles) just leaves the chart blank.
      const seed = seedCandlesRef.current;
      if (seed && seed.length > 0) {
        engine.seed(seed);
        seeded = true;
      }
    } else if (REPLAY_ENABLED) {
      // Dev: replay the recorded fixture instead of the live feed. Dynamic import
      // keeps the fixture out of the production main bundle (only fetched here).
      void import('@/mock/btc_5min_1s.json').then((mod) => {
        if (cancelled) return;
        const candles = mod.default as unknown as ReplayCandle[];
        replay = new ReplayPriceSocket(candles, instrumentSymbol ?? undefined);
        replay.subscribe((raw) => {
          const tick = parseInstrumentTick(raw);
          if (tick) onTick(tick);
        });
      });
    } else if (!instrumentSymbol) {
      mock = new MockPriceSocket(basePriceRef.current);
      mock.subscribe((raw) => {
        const tick = parseInstrumentTick(raw);
        if (tick) onTick(tick);
      });
    }

    return () => {
      cancelled = true;
      if (historyTimeout !== null) clearTimeout(historyTimeout);
      mock?.unsubscribe();
      replay?.unsubscribe();
      tickHandlerRef.current = () => {};
      historyResultRef.current = () => {};
      engine.destroy();
      engineRef.current = null;
      canvas.remove();
    };
    // NOTE: basePrice/targetPrice are intentionally NOT dependencies — they are
    // read via refs (targetPrice every frame), so changing them never rebuilds
    // the chart. The chart re-initialises only when the instrument (or
    // interactivity / frozen mode) changes.
  }, [instrumentSymbol, interactive, isFrozen]);

  return { containerRef };
}
